import React, { useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { CircleCloseIcon } from '@/atoms/Icons/CircleCloseIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { Image } from '@/atoms/Image'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { LabelSM, TitleXXS } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { ExperimentVariation, GBFadeInExperiment } from '@/experimentation'
import { getWebClient } from '@/services/ApolloClient'
import { useGuildMember, useUserGuildTickets } from '@/services/GuildUserService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { getBooleanFromLocalStorage, writeToLocalStorage } from '@/utils/local-storage'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'

export const TicketsNavBanner: React.FC = () => {
  const track = useSafeTrack()
  const { t } = useTranslate('common')
  const router = useSafeRouter()
  const { isGuildMember, loading } = useGuildMember()
  const client = getWebClient()
  const [shouldRender, setShouldRender] = useState(false)
  const { numberAvailable: guildTicketsCount = 0, loading: loadingGuildTickets } = useUserGuildTickets({
    theatricalSlug: slugs.truthAndTreason,
    client,
    skip: !isGuildMember,
  })

  useEffect(() => {
    const hasDismissedBanner = getBooleanFromLocalStorage('hasDismissedTicketsNavBanner')
    const isGuildJoinPage = router.pathname.includes('/guild/join')
    const isGuildPricingPage = router.pathname.includes('/guild/pricing')

    if (!hasDismissedBanner && !isGuildJoinPage && !isGuildPricingPage) {
      if (!loading && isGuildMember) {
        setShouldRender(true)
      }
    }
  }, [router.pathname, isGuildMember, loading])

  const handleTicketsNavBannerDismiss = useCallback(
    (buttonOrigin: boolean) => {
      writeToLocalStorage('hasDismissedTicketsNavBanner', true)
      if (!buttonOrigin) {
        track('Tickets Nav Banner Dismissed')
      }
      setShouldRender(false)
    },
    [track],
  )

  const handleTicketsNavButtonClick = useCallback(() => {
    track('Tickets Nav Banner Button Clicked')
    handleTicketsNavBannerDismiss(true)
  }, [handleTicketsNavBannerDismiss, track])

  return (
    <When condition={shouldRender}>
      <GBFadeInExperiment experimentName="truth-and-treason-tickets-nav-banner" defaultValue={'control'}>
        <ExperimentVariation variation={'variation_1'}>
          <div
            className={classNames(
              'fixed md:absolute inset-x-0 bottom-0 md:bottom-auto md:top-0 z-40 md:z-50 flex flex-col items-center max-w-full group-floating-cta bg-black',
            )}
          >
            <div className="responsive relative h-14 w-full max-w-[1728px] p-3 md:h-16 md:p-4">
              <ResponsiveBackgroundImage
                src={getCloudinaryImageUrl({
                  path: '/v1759271446/angel-studios/nav/Promotional-Banner-BG-Mobile-Image-01.webp',
                  width: 1024,
                })}
                className="absolute inset-0 h-full w-full lg:hidden"
              />
              <ResponsiveBackgroundImage
                src={getCloudinaryImageUrl({
                  path: '/v1759271439/angel-studios/nav/Promotional-Banner-BG-Desk-Image-01.webp',
                  width: 1728,
                })}
                className="absolute inset-0 hidden h-full w-full bg-cover bg-no-repeat lg:block"
              />
              <div className="relative mx-auto flex h-full w-full max-w-[1728px] items-center justify-between">
                <div className="flex flex-row md:gap-[27px]">
                  <div className="flex flex-col items-center gap-2 lg:flex-row lg:gap-[27px]">
                    <Image
                      src={getCloudinaryImageUrl({
                        path: '/v1759272321/angel-studios/nav/Truth_and_Treason_Logo_-_Horizontal-White_1.webp',
                        width: 166,
                      })}
                      alt="Truth and Treason Horizontal Logo"
                      className="max-h-[16px] max-w-[81px] md:max-h-[32px] md:max-w-[166px]"
                      width={166}
                      height={32}
                    />
                    <div>
                      <Image
                        src={getCloudinaryImageUrl({
                          path: '/v1759329678/angel-studios/nav/Tickets-text-mobile.webp',
                          width: 126,
                        })}
                        alt="Tickets Available Now"
                        className="lg:hidden"
                        width={126}
                        height={9}
                      />
                      <Image
                        src={getCloudinaryImageUrl({
                          path: '/v1759329695/angel-studios/nav/Tickets-text-desktop.webp',
                          width: 215,
                        })}
                        alt="Tickets Available Now"
                        className="hidden lg:block"
                        width={215}
                        height={15}
                      />
                    </div>
                  </div>
                  <Image
                    src={getCloudinaryImageUrl({
                      path: '/v1759332132/angel-studios/nav/Truth-and-Treason-Tickets.webp',
                      width: 104,
                    })}
                    alt="Truth and Treason Tickets"
                    className="hidden md:block"
                    width={104}
                    height={48}
                  />
                </div>
                <div className="flex flex-row items-center gap-[35px] md:gap-[42px] lg:gap-[145px]">
                  <div>
                    <When condition={!loadingGuildTickets}>
                      <LinkButton
                        href={`${paths.tickets.index}/${slugs.truthAndTreason}`}
                        variant={'black'}
                        className="rounded-lg border-transparent bg-white/20 px-4 py-2 hover:bg-white/40"
                        onClick={handleTicketsNavButtonClick}
                      >
                        <LabelSM>
                          {guildTicketsCount && guildTicketsCount > 0
                            ? t('claimTickets', 'Claim Tickets')
                            : t('getShowtimes', 'Get Showtimes')}
                        </LabelSM>
                      </LinkButton>
                    </When>
                  </div>
                  <div
                    onClick={() => handleTicketsNavBannerDismiss(false)}
                    className="cursor-pointer rounded-full lg:rounded-none"
                  >
                    <div className="hidden cursor-pointer lg:flex lg:flex-row lg:items-center lg:gap-2">
                      <TitleXXS weight="semibold" color="core-gray-400">
                        {t('close', 'Close')}
                      </TitleXXS>
                      <CloseIcon color="core-gray-400" size={24} className="cursor-pointer" />
                    </div>
                    <div className="lg:hidden">
                      <CircleCloseIcon color="core-gray-400" size={21} className="cursor-pointer" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Spacer to push the site nav below the fixed banner on md+ */}
          <div className="hidden h-16 w-full md:block" aria-hidden="true" />
        </ExperimentVariation>
        <ExperimentVariation variation={'variation_2'}>
          <div
            className={classNames(
              'fixed md:absolute inset-x-0 bottom-0 md:bottom-auto md:top-0 z-40 md:z-50 flex flex-col items-center max-w-full group-floating-cta bg-black',
            )}
          >
            <div className="responsive relative h-14 w-full max-w-[1728px] p-3 md:h-16 md:p-4">
              <ResponsiveBackgroundImage
                src={getCloudinaryImageUrl({
                  path: '/v1759336382/angel-studios/nav/Promotional-Banner-BG-Mobile-Image-02-b.webp',
                  width: 1024,
                })}
                className="absolute inset-0 h-full w-full lg:hidden"
              />
              <ResponsiveBackgroundImage
                src={getCloudinaryImageUrl({
                  path: '/v1759271439/angel-studios/nav/Promotional-Banner-Desk-BG-Image-02.webp',
                  width: 1728,
                })}
                className="absolute inset-0 hidden h-full w-full bg-cover bg-no-repeat lg:block"
              />
              <div className="relative mx-auto flex h-full w-full max-w-[1728px] items-center justify-between">
                <div className="flex flex-row md:gap-[27px]">
                  <div className="flex flex-col items-center gap-2 lg:flex-row lg:gap-[27px]">
                    <Image
                      src={getCloudinaryImageUrl({
                        path: '/v1759272321/angel-studios/nav/Truth_and_Treason_Logo_-_Horizontal-White_1.webp',
                        width: 166,
                      })}
                      alt="Truth and Treason Horizontal Logo"
                      className="max-h-[16px] max-w-[81px] md:max-h-[32px] md:max-w-[166px]"
                      width={166}
                      height={32}
                    />
                    <div>
                      <Image
                        src={getCloudinaryImageUrl({
                          path: '/v1759329678/angel-studios/nav/Tickets-text-mobile.webp',
                          width: 126,
                        })}
                        alt="Tickets Available Now"
                        className="lg:hidden"
                        width={126}
                        height={9}
                      />
                      <Image
                        src={getCloudinaryImageUrl({
                          path: '/v1759329695/angel-studios/nav/Tickets-text-desktop.webp',
                          width: 215,
                        })}
                        alt="Tickets Available Now"
                        className="hidden lg:block"
                        width={215}
                        height={15}
                      />
                    </div>
                  </div>
                  <Image
                    src={getCloudinaryImageUrl({
                      path: '/v1759332132/angel-studios/nav/Truth-and-Treason-Tickets.webp',
                      width: 104,
                    })}
                    alt="Truth and Treason Tickets"
                    className="hidden md:block"
                    width={104}
                    height={48}
                  />
                </div>
                <div className="flex flex-row items-center gap-[35px] md:gap-[42px] lg:gap-[145px]">
                  <div>
                    <When condition={!loadingGuildTickets}>
                      <LinkButton
                        href={`${paths.tickets.index}/${slugs.truthAndTreason}`}
                        variant={'black'}
                        className="rounded-lg border-transparent bg-white/20 px-4 py-2 hover:bg-white/40"
                        onClick={handleTicketsNavButtonClick}
                      >
                        <LabelSM>
                          {guildTicketsCount && guildTicketsCount > 0
                            ? t('claimTickets', 'Claim Tickets')
                            : t('getShowtimes', 'Get Showtimes')}
                        </LabelSM>
                      </LinkButton>
                    </When>
                  </div>
                  <div
                    onClick={() => handleTicketsNavBannerDismiss(false)}
                    className="cursor-pointer rounded-full lg:rounded-none"
                  >
                    <div className="hidden cursor-pointer lg:flex lg:flex-row lg:items-center lg:gap-2">
                      <TitleXXS weight="semibold" color="core-gray-400">
                        {t('close', 'Close')}
                      </TitleXXS>
                      <CloseIcon color="core-gray-400" size={24} className="cursor-pointer" />
                    </div>
                    <div className="lg:hidden">
                      <CircleCloseIcon color="core-gray-400" size={21} className="cursor-pointer" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Spacer to push the site nav below the fixed banner on md+ */}
          <div className="hidden h-16 w-full md:block" aria-hidden="true" />
        </ExperimentVariation>
        <ExperimentVariation variation={'control'}>{null}</ExperimentVariation>
      </GBFadeInExperiment>
    </When>
  )
}
